<template>
        <aside
                class="z-20 hidden w-64 overflow-y-auto md:block flex-shrink-0 bg-white border-r border-gray-200"
        >
                <div class="py-4 text-gray-500">
                        <ul class="mt-2 list-none p-0 m-0">
                                <li
                                        v-for="(node, index) in navigation"
                                        :key="`nav-${index}`"
                                        class="nav-item relative px-6 py-3"
                                >
                                        <span
                                                v-if="isActiveRoute(node)"
                                                class="absolute inset-y-0 left-0 w-1 rounded-tr-lg rounded-br-lg bg-orange-7"
                                                aria-hidden="true"
                                        />
                                        <template v-if="!node.sub">
                                                <router-link
                                                        :to="node.to || '/'"
                                                        class="inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 no-underline bg-transparent hover:text-gray-800"
                                                        :class="isActiveRoute(node) ? 'text-gray-800' : 'text-gray-500'"
                                                >
                                                        <component
                                                                :is="getIconComponent(node.icon)"
                                                                class="w-5 h-5"
                                                        />
                                                        <span class="ml-4">{{ node.name }}</span>
                                                </router-link>
                                        </template>
                                        <template v-else>
                                                <button
                                                        class="inline-flex items-center justify-between w-full text-sm font-semibold transition-colors duration-150 bg-transparent border-none cursor-pointer p-0 hover:text-gray-800"
                                                        :class="isSubmenuOpen(node) ? 'text-gray-800' : 'text-gray-500'"
                                                        @click="toggleSubmenu(index)"
                                                >
                                                        <span class="inline-flex items-center">
                                                                <component
                                                                        :is="getIconComponent(node.icon)"
                                                                        class="w-5 h-5"
                                                                />
                                                                <span class="ml-4">{{ node.name }}</span>
                                                        </span>
                                                        <svg
                                                                class="w-4 h-4 transition-transform duration-200"
                                                                :class="{ 'rotate-180': openMenus[index] }"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                        >
                                                                <path
                                                                        fill-rule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clip-rule="evenodd"
                                                                />
                                                        </svg>
                                                </button>
                                                <ul
                                                        v-show="openMenus[index]"
                                                        class="mt-2 text-sm font-medium rounded-md bg-gray-50 list-none p-2 pl-10 shadow-inner"
                                                >
                                                        <li
                                                                v-for="(subNode, subIndex) in node.sub"
                                                                :key="`sub-${subIndex}`"
                                                                class="bg-transparent border-none py-1.5 m-0 shadow-none"
                                                        >
                                                                <router-link
                                                                        :to="subNode.to || '/'"
                                                                        class="w-full block transition-colors duration-150 no-underline bg-transparent hover:text-orange-7"
                                                                        :class="isActiveSubRoute(subNode) ? 'text-gray-800' : 'text-gray-500'"
                                                                >
                                                                        {{ subNode.name }}
                                                                </router-link>
                                                        </li>
                                                </ul>
                                        </template>
                                </li>
                        </ul>
                </div>
        </aside>
</template>

<script setup>
import { reactive, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import menu from '../../../data/menu';
import { iconMap } from '../../icons';
import { useModuleAccess } from 'composables/useModuleAccess';

const route = useRoute();
const { canAccessModule } = useModuleAccess();

const filteredNavigation = computed(() => {
        return menu.main
                .map((node) => {
                        if (!node.sub) {
                                return node;
                        }
                        const filteredSub = node.sub.filter(
                                (subNode) => !subNode.module || canAccessModule(subNode.module),
                        );
                        if (filteredSub.length === 0) {
                                return null;
                        }
                        return { ...node, sub: filteredSub };
                })
                .filter(Boolean);
});

const navigation = filteredNavigation;
const openMenus = reactive({});

watch(
        [() => route.path, filteredNavigation],
        () => {
                filteredNavigation.value.forEach((node, index) => {
                        if (node.sub && isSubmenuOpen(node)) {
                                openMenus[index] = true;
                        }
                });
        },
        { immediate: true },
);

function toggleSubmenu(index) {
        openMenus[index] = !openMenus[index];
}

function isActiveRoute(node) {
        if (node.to) {
                return route.path === node.to || route.path.startsWith(node.to + '/');
        }
        if (node.sub) {
                return node.sub.some((sub) => route.path.startsWith(sub.to));
        }
        return false;
}

function isSubmenuOpen(node) {
        if (node.sub) {
                return node.sub.some((sub) => route.path.startsWith(sub.to));
        }
        return false;
}

function isActiveSubRoute(node) {
        return route.path.startsWith(node.to);
}

function getIconComponent(iconClass) {
        return iconMap[iconClass] || iconMap['si si-speedometer'];
}
</script>

<style scoped>
aside {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        display: none !important;
}

@media (min-width: 768px) {
        aside {
                display: block !important;
        }
}

aside ul {
        list-style: none !important;
        padding-left: 0 !important;
        margin-bottom: 0 !important;
        margin-top: 0 !important;
}

aside li {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        margin: 0 !important;
}

aside li::before {
        display: none !important;
        content: none !important;
}

aside a {
        all: unset !important;
        cursor: pointer !important;
        display: inline-flex !important;
        align-items: center !important;
        width: 100% !important;
        font-size: 0.875rem !important;
        font-weight: 600 !important;
        transition: color 0.15s ease-in-out !important;
}

aside a:link,
aside a:visited {
        color: inherit !important;
        text-decoration: none !important;
}

aside a:hover,
aside a:active {
        color: #1f2937 !important;
        text-decoration: none !important;
        outline: none !important;
        box-shadow: none !important;
        background: transparent !important;
}

aside a:focus-visible {
        color: #1f2937 !important;
        outline: 2px solid #9333ea !important;
        outline-offset: 2px !important;
        border-radius: 0.25rem !important;
}

aside button {
        all: unset !important;
        cursor: pointer !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        width: 100% !important;
        font-size: 0.875rem !important;
        font-weight: 600 !important;
        transition: color 0.15s ease-in-out !important;
}

aside button:hover {
        color: #1f2937 !important;
}

aside button:focus {
        outline: none !important;
        box-shadow: none !important;
}

aside button:focus-visible {
        outline: 2px solid #9333ea !important;
        outline-offset: 2px !important;
        border-radius: 0.25rem !important;
}

aside svg {
        width: 1.25rem !important;
        height: 1.25rem !important;
        flex-shrink: 0 !important;
}

aside ul ul {
        background-color: #f9fafb !important;
        border-radius: 0.375rem !important;
        padding: 0.5rem !important;
        padding-left: 2.5rem !important;
        margin-top: 0.5rem !important;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

aside ul ul li {
        padding: 0.375rem 0 !important;
}

aside ul ul a {
        font-size: 0.875rem !important;
        font-weight: 500 !important;
}

.nav-item {
        position: relative !important;
        padding: 0.75rem 1.5rem !important;
        transition: background-color 0.15s ease-in-out !important;
}

.nav-item:hover {
        background-color: #f9fafb !important;
}

.nav-item:hover a,
.nav-item:hover button {
        color: #1f2937 !important;
}

aside ul ul li:hover {
        background-color: transparent !important;
}

aside ul ul li:hover a {
        color: #9333ea !important;
}
</style>
